import { useEffect, useRef, useState } from 'react'

/**
 * Live resource counter: starts from the server-computed `base`, moves by
 * `perHour` client-side every second, capped at `capacity` and floored at zero.
 * Resets whenever the inputs change (e.g. when the underlying query refetches).
 *
 * Shared between the big ResourceBar (Island page) and the slim ResourceBanner
 * (global header) so both stay in lock-step.
 *
 * `perHour` is SIGNED and a negative rate is a real case, not a guard: under the
 * branle-bas (fiche #27) the island's rum rate becomes the cellar drain, so the
 * counter must tick DOWN and mirror the server's own lazy computation
 * ({@see \App\Domain\Economy\ResourceCalculator}). It stops at zero because the
 * server never lets a resource go negative either: the mobilisation stands down
 * at the exact instant a reserve empties.
 */
export function useResourceTick(base: number, perHour: number, capacity: number): number {
  const fetchedAt = useRef(Date.now())
  const [value, setValue] = useState(base)

  useEffect(() => {
    fetchedAt.current = Date.now()
    setValue(base)

    // Only a flat rate has nothing to animate. A negative one drains.
    if (perHour === 0) return

    const id = window.setInterval(() => {
      const elapsedHours = (Date.now() - fetchedAt.current) / 3_600_000
      const next = Math.floor(base + perHour * elapsedHours)
      const capped = capacity > 0 ? Math.min(capacity, next) : next
      setValue(Math.max(0, capped))
    }, 1000)

    return () => window.clearInterval(id)
  }, [base, perHour, capacity])

  return value
}
